import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useMediaQuery,
  Popper,
  ClickAwayListener,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  LayoutDashboard,
  HandCoins,
  ChartNoAxesCombined,
  CirclePercent,
  Ticket,
  Calendar,
  ClipboardCheck,
  Settings,
  Presentation,
  Bell,
} from "lucide-react";
import GroupIcon from "@mui/icons-material/Group";
import logo from "../assets/united-grooves-logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import { HealthiconsITrainingClass } from "./Healthicontraining";
import { HugeiconsStudents } from "./HugeiconsStudents";
import { useSidebar } from "./sidebarcontext";
import { useQuery } from "@apollo/client";
import { ME } from "../graphql/query";
import { useMemo } from "react";

const allMenuItems = [
  { text: "Dashboard", icon: <LayoutDashboard />, path: "/dashboard" },
  { text: "Billing Dashboard", icon: <HandCoins />, path: "/billing" },
  { text: "Reports Dashboard", icon: <ChartNoAxesCombined />, path: "/reports" },
  { text: "Offers", icon: <CirclePercent />, path: "/offers" },
  { text: "Instructors", icon: <Presentation />, path: "/instructors" },
  { text: "Admin", icon: <GroupIcon />, path: "/admin" },
  {
    text: "Classes",
    icon: <HealthiconsITrainingClass width={30} height={40} />,
    path: "/classes",
  },
  { text: "Calendar", icon: <ClipboardCheck />, path: "/calendar" },
  {
    text: "Students",
    icon: <HugeiconsStudents width={30} height={40} />,
    path: "/students",
  },
  { text: "Passes", icon: <Ticket />, path: "/passes" },
  { text: "Events", icon: <Calendar />, path: "/events" },
  { text: "Notifications", icon: <Bell />, path: "/notifications" },
  { text: "Settings", icon: <Settings />, path: "/settings" },
];
export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { anchorEl, isOpen, closeSidebar } = useSidebar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const { data } = useQuery(ME, {
    fetchPolicy: "cache-first",
  });

  const storedUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch {
      return null;
    }
  })();

  const userRole =
    data?.me?.role?.toUpperCase() || storedUser?.role?.toUpperCase() || "";

  const menuItems = useMemo(() => {
    if (userRole === "INSTRUCTOR") {
      return allMenuItems.filter((item) =>
        ["Dashboard", "Classes", "Calendar", "Notifications", "Settings"].includes(
          item.text,
        ),
      );
    }

    if (userRole === "ADMIN") {
      return allMenuItems.filter(
        (item) => !["Billing Dashboard", "Reports Dashboard", "Admin"].includes(item.text),
      );
    }

    if (userRole === "USER") {
      return allMenuItems.filter((item) =>
        [
          "Dashboard",
          "Classes",
          "Passes",
          "Calendar",
          "Events",
          "Notifications",
          "Settings",
        ].includes(item.text),
      );
    }
    return allMenuItems;
  }, [userRole]);

  const sidebarContent = (
    <Box
      sx={{
        width: isTablet ? "200px" : "240px",
        boxSizing: "border-box",
        opacity: 1,
        bgcolor: "#fff",
        borderRight: "1.5px solid #e5e7eb",
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        position: isMobile ? "relative" : "fixed",
        top: 0,
        left: 0,
        overflow: "hidden",
      }}
    >
      {!isMobile && (
        <Box
          sx={{
            padding: isTablet ? "24px 12px 12px" : "32px 21px 16px",
            bgcolor: "#fff",
            borderBottom: "1px solid transparent", // Optional: add border if needed
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5} px={1}>
            <Box
              component="img"
              src={logo}
              alt="United Grooves Logo"
              sx={{
                width: 24,
                height: 24,
                objectFit: "contain",
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "18px",
                color: "#111827",
                lineHeight: 1.2,
              }}
            >
              United Grooves
            </Typography>
          </Box>
        </Box>
      )}

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          padding: isTablet ? "0 12px 24px" : "0 21px 32px",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <List sx={{ px: 0 }}>
          {menuItems.map((item, index) => {
            const isActive =
              location.pathname === item.path ||
              location.pathname.startsWith(item.path + "/");
            return (
              <ListItemButton
                key={index}
                onClick={() => {
                  navigate(item.path);
                  closeSidebar();
                }}
                sx={{
                  borderRadius: "8px",
                  mb: 0.5,
                  py: 1.2,
                  px: 2,
                  bgcolor: isActive ? "#a3d65c" : "transparent",
                  color: isActive ? "#fff" : "#4B5563",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    bgcolor: isActive ? "#a3d65c" : "rgba(0, 0, 0, 0.04)",
                    color: isActive ? "#fff" : "#111827",
                    "& .MuiListItemIcon-root": {
                      color: isActive ? "#fff" : "#111827",
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? "#fff" : "#6B7280",
                    minWidth: "40px",
                    "& svg": {
                      fontSize: "20px",
                      width: "20px",
                      height: "20px",
                    },
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    sx: {
                      fontFamily: "Roboto",
                      fontWeight: 600,
                      fontSize: "14px",
                      lineHeight: "20px",
                      letterSpacing: "-0.01em",
                    },
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>
    </Box>
  );
  return isMobile ? (
    <Popper
      open={isOpen}
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[
        {
          name: "offset",
          options: {
            offset: [0, 8], // space below header
          },
        },
      ]}
      sx={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={closeSidebar}>
        <Paper
          sx={{
            width: isTablet ? 230 : 278,
            maxHeight: "85vh",
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            borderRadius: "16px",
            boxShadow: "0px 12px 30px rgba(0,0,0,0.15)",
          }}
        >
          {sidebarContent}
        </Paper>
      </ClickAwayListener>
    </Popper>
  ) : (
    sidebarContent
  );
}
